/**
 * A tiny, dependency-free fuzzy matcher for small in-memory lists (a board's people
 * and groups, an org's groups - dozens, not thousands). Two tiers, exact always beats
 * fuzzy: an **exact substring** match scores highest (with a prefix / word-start /
 * whole-string bonus), and when the query doesn't appear verbatim we fall back to a
 * **subsequence** match (every query character in order) so typos and partials still
 * find their target. `positions` are the matched indices, so the UI can highlight the
 * hit. Pure, no I/O, fully unit-tested - this is the search behaviour the Access picker
 * (and any other entity picker) leans on.
 */

export interface FuzzyResult {
  /** Higher is a better match. Compare only within one query's result set. */
  score: number;
  /** Indices into the target that the query matched, ascending, for highlighting. */
  positions: number[];
}

// Characters that start a new "word", so a match landing right after one reads as a
// real word hit ("pl" -> "Platform", "des" -> "...-design") rather than a mid-word fluke.
const BOUNDARY = /[\s\-_/.@]/;

function isBoundary(text: string, i: number): boolean {
  return i === 0 || BOUNDARY.test(text[i - 1]!);
}

/**
 * Score how well `query` matches `target`, or `null` when it doesn't match at all.
 * An empty query matches everything with a neutral score (callers keep original order).
 */
export function fuzzyMatch(query: string, target: string): FuzzyResult | null {
  const q = query.trim().toLowerCase();
  if (q === '') return { score: 0, positions: [] };
  const t = target.toLowerCase();

  // Tier 1 - exact substring. The strongest signal; the big base keeps any verbatim hit
  // ahead of every subsequence hit. Earlier, word-aligned, and whole-string wins rank up.
  const at = t.indexOf(q);
  if (at !== -1) {
    const positions = Array.from({ length: q.length }, (_, k) => at + k);
    let score = 10_000 - at;
    if (at === 0)
      score += 4_000; // prefix of the whole string
    else if (isBoundary(t, at)) score += 2_000; // start of a word inside it
    if (q.length === t.length) score += 2_000; // the entire string
    return { score, positions };
  }

  // Tier 2 - subsequence. Every query char must appear in order; reward contiguous runs
  // and word-boundary landings, penalise the gaps between hits so tight matches win.
  const positions: number[] = [];
  let cursor = 0;
  let score = 0;
  let prev = -1;
  for (const ch of q) {
    let found = -1;
    while (cursor < t.length) {
      if (t[cursor] === ch) {
        found = cursor;
        break;
      }
      cursor++;
    }
    if (found === -1) return null; // not a subsequence - no match
    if (prev >= 0) {
      if (found === prev + 1)
        score += 10; // contiguous with the previous hit
      else score -= Math.min(found - prev - 1, 4); // gap, capped so long names aren't crushed
    }
    if (isBoundary(t, found)) score += 6;
    positions.push(found);
    prev = found;
    cursor++;
  }
  score -= positions[0]!; // an earlier first hit is marginally better
  return { score, positions };
}

export interface Ranked<T> extends FuzzyResult {
  item: T;
}

/**
 * Match every item against the query by its `key` string, drop the misses, and sort
 * best-first. Ties keep the input order (stable sort), so an empty query returns the
 * list untouched - the caller controls the resting order (e.g. people then groups).
 */
export function fuzzyRank<T>(
  query: string,
  items: readonly T[],
  key: (item: T) => string,
): Ranked<T>[] {
  const ranked: Ranked<T>[] = [];
  for (const item of items) {
    const m = fuzzyMatch(query, key(item));
    if (m) ranked.push({ item, score: m.score, positions: m.positions });
  }
  ranked.sort((a, b) => b.score - a.score);
  return ranked;
}
